import { useEffect, useState, type RefObject } from 'react';

export function useOnClickOutside(ref: RefObject<HTMLElement>, handler: (e: Event) => void) {
    useEffect(() => {
        return attachListeners((event: MouseEvent | TouchEvent) => {
            if (!ref.current || ref.current.contains(event.target as Node)) {
                return;
            }
            handler?.(event);
        });
    }, []);
}

function attachListeners(handler: (e: MouseEvent | TouchEvent) => void): () => void {
    const listener = (event: MouseEvent | TouchEvent) => {
        handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
    };
}

// breakpoints from src/styles/RemotelyTheme/_mobile.scss
export enum BREAKPOINTS {
    sm = '(max-width: 700px)',
    sm_portrait = '(max-width: 700px) and (orientation: portrait)',
    md = '(max-width: 1080px)',
    lg = '(max-width: 1440px)',
    xl = '(max-width: 2560px)',
    xxl = '(min-width: 2561px)',
}

export function breakpointIs(bp: BREAKPOINTS): [boolean, MediaQueryList | null] {
    if (!window.matchMedia) return [false, null];

    const mediaQuery = window.matchMedia(bp);

    return [mediaQuery.matches, mediaQuery];
}

export function useBreakpoint(bp: BREAKPOINTS, callback?: (matches: boolean) => void) {
    const [isBreakpoint, setIsBreakpoint] = useState(false);

    useEffect(() => {
        if (!window.matchMedia) return;

        const [matches, mediaQuery] = breakpointIs(bp);

        setIsBreakpoint(matches);
        callback?.(matches);

        const handler = (event: MediaQueryListEvent) => {
            setIsBreakpoint(event.matches);
            callback?.(event.matches);
        };

        if (!mediaQuery) return;

        mediaQuery.addEventListener('change', handler);
        return () => {
            mediaQuery.removeEventListener('change', handler);
        };
    }, [bp, callback]);

    return isBreakpoint;
}

function getScrollRoot(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;
    while (parent) {
        const { overflowY } = getComputedStyle(parent);
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
}

function getScrollOffset(scrollRoot: HTMLElement | null): number {
    if (scrollRoot) {
        return scrollRoot.scrollTop;
    }
    return window.scrollY || document.documentElement.scrollTop;
}

export function shouldActivateStickyOnTop(
    entry: Pick<
        IntersectionObserverEntry,
        'boundingClientRect' | 'rootBounds' | 'intersectionRatio'
    >,
    scrollOffset: number,
): boolean {
    const rect = entry.boundingClientRect;
    const rootBottom = entry.rootBounds?.bottom ?? window.innerHeight;
    const closerToTop = rect.top <= rootBottom - rect.bottom;
    return entry.intersectionRatio < 1 && closerToTop && scrollOffset > 0;
}

function applyIntersectOnTopClass(
    entry: IntersectionObserverEntry,
    targetEl: HTMLElement,
    stickyClassName: string,
    scrollRoot: HTMLElement | null,
    onStickyChange?: (isSticky: boolean) => void,
) {
    if (
        shouldActivateStickyOnTop(entry, getScrollOffset(scrollRoot)) &&
        !targetEl.classList.contains(stickyClassName)
    ) {
        targetEl.classList.add(stickyClassName);
        onStickyChange?.(true);
    } else if (entry.isIntersecting && targetEl.classList.contains(stickyClassName)) {
        targetEl.classList.remove(stickyClassName);
        onStickyChange?.(false);
    }
}

// appends given class name when element becomes sticky (assuming has a sticky position in CSS)
export function useIntersectOnTop({
    trigger,
    target,
    stickyClassName = 'is-intersecting-top',
    onStickyChange,
}: {
    trigger: HTMLElement | null;
    target?: HTMLElement | null;
    stickyClassName?: string;
    onStickyChange?: (isSticky: boolean) => void;
}) {
    useEffect(() => {
        const targetEl = target ?? trigger;
        if (!targetEl || !window.IntersectionObserver || !trigger) return;

        const scrollRoot = getScrollRoot(trigger);

        const observer = new IntersectionObserver(
            ([e]) =>
                applyIntersectOnTopClass(e, targetEl, stickyClassName, scrollRoot, onStickyChange),
            { threshold: [1], root: scrollRoot },
        );

        const scheduleRecheck = () => {
            requestAnimationFrame(() => {
                observer.unobserve(trigger);
                observer.observe(trigger);
            });
        };

        scheduleRecheck();

        const mutationObserver = new MutationObserver(scheduleRecheck);

        for (let parent = trigger.parentElement; parent; parent = parent.parentElement) {
            mutationObserver.observe(parent, { childList: true });
        }

        return () => {
            observer.disconnect();
            mutationObserver.disconnect();
        };
    }, [trigger, target, stickyClassName, onStickyChange]);
}
